// Shared helpers for JSON HTTP clients: turn a non-ok Response into a descriptive
// Error (preferring the body's `error` field, falling back to the status), and
// assert a Response is ok. Kept status-aware so a caller's message includes the
// HTTP code even when the body isn't JSON.

import { recordApiVersion } from './apiVersion';
import { logger } from './logger';

async function parseError(res: Response, what: string): Promise<never> {
  let detail = '';
  try {
    const body = await res.json();
    detail = typeof body?.error === 'string' ? `: ${body.error}` : '';
  } catch {
    /* non-JSON body */
  }
  throw new Error(`${what} failed (${res.status})${detail}`);
}

export async function expectOk(res: Response, what: string): Promise<Response> {
  recordApiVersion(res);
  if (!res.ok) await parseError(res, what);
  return res;
}

export class ContractViolationError extends Error {
  constructor(public readonly endpoint: string, issueSummary: string) {
    super(`Contract violation on ${endpoint}: ${issueSummary}`);
    this.name = 'ContractViolationError';
  }
}

/** schema.parse, with drift turned into ContractViolationError. Split out of
 *  `fetchParsed` for the calls that must keep their own non-ok handling — a
 *  mutation whose server message reaches the user verbatim — while still owing
 *  the contract a parse on the success path. */
export function parseContract<T>(schema: { parse(data: unknown): T }, body: unknown, what: string): T {
  try {
    return schema.parse(body);
  } catch (err) {
    const issueSummary = err instanceof Error ? err.message : String(err);
    // Log here, not just at call sites — a call site that swallows the thrown
    // error (or doesn't await it) would otherwise leave the drift invisible.
    logger.error('contract violation:', what, issueSummary);
    throw new ContractViolationError(what, issueSummary);
  }
}

/** fetch → expectOk → schema.parse. Shape drift throws ContractViolationError
 *  (issues go to the logger at the call site, never raw to users). */
export async function fetchParsed<T>(
  schema: { parse(data: unknown): T },
  input: string,
  init: RequestInit,
  what: string,
): Promise<T> {
  const res = await fetch(input, init);
  await expectOk(res, what);
  return parseContract(schema, await res.json(), what);
}
